import { DEFAULT_REGION_ID } from "@/atoms/activeRegion";
import { getSpotPageByRegionBreak } from "@/lib/spotPages";

/** Northeast Florida break used as the Florida home spot. */
export const FLAGLER_REGION_ID = "Northeast Florida";
export const FLAGLER_CAMERA_ID = "flagler-beach-pier";

const FLORIDA_REGION_IDS = new Set([
  "Northeast Florida",
  "Central Florida",
  "Space Coast",
  "South Florida",
]);

const LAST_VIEWED_REGION_KEY = "lastViewedRegionId";

export function isFloridaRegion(regionId: string | null | undefined): boolean {
  return Boolean(regionId && FLORIDA_REGION_IDS.has(regionId));
}

/** Canonical Flagler Beach report. Falls back to the slug if config is missing. */
export function flaglerBeachPath(): string {
  return (
    getSpotPageByRegionBreak(FLAGLER_REGION_ID, FLAGLER_CAMERA_ID)?.path ??
    "/flagler-beach-surf-report"
  );
}

export function readLastViewedRegionId(): string | null {
  try {
    const value = localStorage.getItem(LAST_VIEWED_REGION_KEY);
    return value ? value : null;
  } catch {
    return null;
  }
}

export function writeLastViewedRegionId(regionId: string): void {
  try {
    localStorage.setItem(LAST_VIEWED_REGION_KEY, regionId);
  } catch {
    // private mode / blocked storage
  }
}

export function clearLastViewedRegionId(): void {
  try {
    localStorage.removeItem(LAST_VIEWED_REGION_KEY);
  } catch {
    // ignore
  }
}

/**
 * Where to send a visitor who has not starred a home spot, when their last
 * region was in Florida. Other regions return null so the Portugal fallback
 * stays in place.
 */
export function implicitFloridaHomePath(
  lastViewedRegionId: string | null,
): string | null {
  if (!isFloridaRegion(lastViewedRegionId)) return null;
  return flaglerBeachPath();
}

/**
 * Cold start on `/` only. After the visitor picks a region this session,
 * `/` keeps that region instead of pulling them back to Flagler.
 */
export function coldStartFloridaHomePath(
  activeRegionId: string,
  lastViewedRegionId: string | null,
): string | null {
  if (activeRegionId !== DEFAULT_REGION_ID) return null;
  return implicitFloridaHomePath(lastViewedRegionId);
}
